import React, {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {IconSource} from "../../../shared/enums/iconSource";
import NavigationButton from "../../atoms/button/navigationButton";
import FilePicker from "../../molecules/filePicker/filePicker";
import EditField from "../../molecules/editField/editField";
import ImageEditor from "../../molecules/imageEditor/imageEditor";
import {
  getImageEditorOptions,
  ImageEditorOptions
} from "../../molecules/imageEditor/imageEditorOptions";
import {ImageEditorObservable} from "../../../stores/observables/imageEditorObservable";
import "./imagePreviewWithEditor.sass";
import {observer} from "mobx-react-lite";
import {TextObservable} from "../../../stores/observables/textObservable";

export interface IImagePreviewWithEditor {
  state: ImageEditorObservable;
  label: string;
  chooseImageButtonText?: string;
  chooseImageButtonIcon?: IconSource;
  imageDescription?: TextObservable;
  acceptedFileTypes?: string;
  options?: ImageEditorOptions;
}

const ImagePreviewWithEditor: React.FC<IImagePreviewWithEditor> = ({
  state,
  label,
  chooseImageButtonText,
  chooseImageButtonIcon,
  imageDescription,
  acceptedFileTypes = "image/*",
  options
}) => {
  const {t} = useTranslation(["translation"]);

  const defaultedOptions = getImageEditorOptions(options);

  // Ensure our options match between ui and state
  useEffect(() => {
    state.setImageOptions(defaultedOptions);
  }, [state, defaultedOptions]);

  const validationMessage = (() => {
    switch (state.validationState) {
      case "isRequiredAndEmpty":
        return t("translation:controls.image.error");
      case "isInvalidFileType":
        return t("translation:controls.image.invalidFileFormat");
      case "isAboveMaxFileSize":
        return t("translation:controls.image.overFileSizeLimit", {
          maxImageFileSizeMegabytes: 5
        });
      case "isAboveMaxPixels":
        return t("translation:controls.image.aboveMaxResolution", {
          maxWidthAndHeight: defaultedOptions.maxSizeInPixels
        });
      case "isBelowMinWidth":
      case "isBelowMinHeight":
        return t("translation:controls.image.belowMinResolution");
      default:
        return "";
    }
  })();

  const croppedImageSrc = useMemo(
    () =>
      state.croppedImage ? window.URL.createObjectURL(state.croppedImage) : "",
    [state.croppedImage]
  );
  const selectedImageSrc = useMemo(
    () =>
      state.selectedImage
        ? window.URL.createObjectURL(state.selectedImage)
        : "",
    [state.selectedImage]
  );

  // we must tidy up any "working files" for memory management
  useEffect(() => {
    return () => {
      if (croppedImageSrc) {
        window.URL.revokeObjectURL(croppedImageSrc);
      }
    };
  }, [croppedImageSrc]);
  useEffect(() => {
    return () => {
      if (selectedImageSrc) {
        window.URL.revokeObjectURL(selectedImageSrc);
      }
    };
  }, [selectedImageSrc]);

  const renderFilePicker = (): JSX.Element => {
    return (
      <FilePicker
        isValid={state.isValid}
        text={chooseImageButtonText}
        icon={chooseImageButtonIcon}
        acceptedFileTypes={acceptedFileTypes}
        onFilePicked={(file) => state.setSelectedImage(file)}
      />
    );
  };

  const renderImage = (): JSX.Element => {
    return (
      <div className="image-container">
        <img src={croppedImageSrc} alt={imageDescription?.text} />
        <div>
          <NavigationButton
            icon={IconSource.edit}
            tooltip={t("translation:controls.image.editImage")}
            onClick={state.openImageEditor}
          />
          <NavigationButton
            icon={IconSource.delete}
            tooltip={t("translation:controls.image.clearImage")}
            onClick={state.clearAllImages}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={"image-preview-editor"}>
      <EditField
        label={label}
        validationMessage={validationMessage}
        showValidationMessage={!state.isValid}
        isRequired={state.isRequired}
      >
        {state.hasCroppedImage ? renderImage() : renderFilePicker()}
      </EditField>
      {state.hasSelectedImage && (
        <ImageEditor
          isOpen={state.isImageEditorOpen}
          imageSrc={selectedImageSrc}
          onSaveClicked={state.onSaveClicked}
          onCancelClicked={state.onCancelClicked}
          options={options}
        />
      )}
    </div>
  );
};
export default observer(ImagePreviewWithEditor);
